import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Card, CardContent, Grid, ListItem, ListItemText } from '@mui/material';
import { SimTypography } from 'components/SimTypography';
import { SimButton } from 'components/SimButton';
import { SimSlider } from 'components/SimSlider';
import { CheckIcon } from 'components/icons/CheckIcon';

const slides = [
	{
		slideTitle: 'Modified Checklist for Autism in Toddlers, Revised (M-CHAT-R)',
		subTitles: [
			'M-CHAT-R screener is a series of 20 questions about child’s behavior.',
			'It is best suited for toddlers between 16 and 30 months of age.',
			'Screening results and any related concerns should be discussed with pediatrician.'
		],
		bulletOptions: [
			'Developed by neuro psychologists and a clinical psychologist',
			'Can be taken online for parents and caregivers.',
			'Helps spot the right target-areas of ABA therapy, if needed',
			'Covers all child development areas with auditory, social and play behavior',
			'Like all methods, cannot be assumed to be 100% accurate'
		]
	},
	{
		slideTitle: 'Autism Diagnosis Interview – Revised (ADI-R)',
		subTitles: [
			'ADI-R is a clinical autism diagnostic tool.',
			'It is best suited for for children and adults with mental age of 18+ months.',
			'Screening results and any related concerns should be discussed with pediatrician.'
		],
		bulletOptions: [
			'Focuses on reciprocal social interaction patterns',
			'Studies communication and language skills benchmarked to age',
			'Detects restricted, repetitive, stereotyped interests and behaviors.',
			'Based on interview done by a trained researcher or psychologist.',
			'Works well with ADOS (Autism Diagnostic Observation Schedule)'
		]
	},
	{
		slideTitle: 'Childhood Autism Rating Scale (CARS)',
		subTitles: [
			'CARS is a diagnostic assessment based on behavior scale that rates individuals from normal to severe.',
			'It is best suited for toddlers of 2+ years. ',
			' It focuses on differenting autism from other developmental delays such as intellectual disability.'
		],
		bulletOptions: [
			'Focuses on five characteristic abilities of the child',
			'Studies communication and language skills benchmarked to age',
			'Detects restricted, repetitive, stereotyped interests and behaviors.',
			'Focuses on reciprocal social interaction patterns',
			'Works on a 15-module format to cover all aspects of differentiation.'
		]
	},
	{
		slideTitle: 'Autism Diagnostic Observation Schedule – Generic (ADOS-G)',
		subTitles: [
			'ADOS-G is a semi-structured & standardized assessment consisting of four 30-minute modules.',
			'It is suited for toddlers to adults, depending on the mental age',
			' Each module is designed for individual’s level of expressive language.'
		],
		bulletOptions: [
			'Focuses on social interaction, communication and play',
			'Studies the child/’s current functioning through a structured play session.',
			'Often done in combination with ADI-R test',
			'Focuses on reciprocal social interaction patterns',
			'Studies communication and language skills benchmarked to age'
		]
	},
	{
		slideTitle: 'Gilliam Autism Rating Scale – 2nd Edition (GARS-2)',
		subTitles: [
			'GARS-2 assists teachers, parents, and clinicians in identifying and diagnosing autism and it’s severity ',
			'It is suited to individual ages 3 through 22.',
			'Is is usually completed in 5–10 minutes, and can be taken offline'
		],
		bulletOptions: [
			'Focuses on social interaction amd restricted behavior patterns',
			'Reflects and utilizes the items and subscales of the DSM-5',
			'ranks scores based on the individuals in the same group (norm group).',
			'A norm group could be categorized based on age, socioeconomic status, race/ethnicity etc.',
			'Fairly updated & recognized since 2013, with more updates coming up'
		]
	}
];

const FSSAutismAssessment = () => {
	const theme = useTheme();

	return (
		<Box
			sx={{
				position: 'relative',
				boxShadow: 0
			}}>
			<Box
				position={'relative'}
				zIndex={2}
				paddingX={2}
				paddingTop={4} // needed to align slides to left-side content
				marginX={{ xs: 'auto', sm: 'auto', md: '0px' }}
				backgroundColor={'none'}
				borderRadius={3}>
				<Box
					maxWidth={{ xs: '75vw', sm: '85vw', md: '68vw', lg: '68vw' }}
					marginX={'auto'}
					paddingX={'auto'}
					marginY={0}
					paddingY={0}>
					<Box
						marginX={'auto'}
						paddingX={'auto'}
						marginY={0}
						paddingY={0}
						sx={{
							height: { xs: 'auto', md: 1 },
							'& .slick-slide img': {
								objectFit: 'cover'
							},
							'& .slick-list, & .slick-slider, & .slick-track, & .slick-slide > div': {
								height: { xs: 'auto', md: 1 }
							},
							'& .slick-dots': {
								marginTop: { xs: 15, sm: 10, md: 10, lg: 8 },
								top: { xs: '84%', sm: '90%', md: '87%' },
								height: '25px'
							},
							'& .slick-prev': {
								zIndex: 3,
								bottom: 0,
								top: '100%',
								left: '0%',
								marginLeft: 0,
								marginRight: '32px',
								right: 0,
								transform: `translate(-100%, calc(-100% - ${theme.spacing(2)}))`,
								marginTop: { xs: '15px', sm: '15px', md: '15px', lg: '15px' },
								width: 62,
								height: '100%',
								'&:before': {
									fontSize: { xs: 26, sm: 26, md: 52, lg: 52 },
									color: alpha(theme.palette.primary.main, 0.5)
								}
							},
							'& .slick-next': {
								zIndex: 3,
								bottom: 0,
								top: '100%',
								left: '100%',
								marginLeft: '62px',
								right: 0,
								transform: `translate(-100%, calc(-100% - ${theme.spacing(2)}))`,
								marginTop: { xs: '15px', sm: '15px', md: '15px', lg: '15px' },
								width: 62,
								height: '100%',
								'&:before': {
									fontSize: { xs: 26, sm: 26, md: 52, lg: 52 },
									color: alpha(theme.palette.primary.main, 0.5)
								}
							}
						}}>
						<SimSlider
							options={{
								arrows: false,
								slidesToShow: 1,
								speed: 500,
								autoplay: false
							}}>
							{slides.map((item, i) => (
								<Box key={i}>
									<Card
										sx={{
											display: 'flex',
											flexDirection: {
												xs: 'column',
												md: (i + 1) % 2 === 0 ? 'row' : 'row-reverse'
											},
											boxShadow: 0,
											background: 'transparent'
										}}>
										<CardContent
											sx={{
												display: 'flex',
												alignItems: 'left',
												marginLeft: { sx: 0, md: 2 }
											}}>
											<Box>
												<Box>
													<SimTypography variant={'h4'} sx={{ fontWeight: 700 }} gutterBottom>
														{item.slideTitle}
													</SimTypography>

													{item.subTitles?.length && (
														<SimTypography
															color={theme.palette.text.secondary}
															sx={{ mb: 3 }}>
															{item.subTitles.map(subTitle => (
																<Box key={subTitle}>{subTitle}</Box>
															))}
														</SimTypography>
													)}
												</Box>

												{item.bulletOptions?.length && (
													<Grid container spacing='2px'>
														{item.bulletOptions?.map((option, idx) => (
															<Grid item xs={12} key={idx}>
																<Box
																	component={ListItem}
																	disableGutters
																	width={'auto'}
																	padding={0}
																	margin={0}
																	display={'flex'}
																	flexDirection={'row'}
																	alignItems={'flex-start'}
																	justifyItems={'left'}>
																	<CheckIcon />
																	<Box color={theme.palette.text.secondary}>
																		<ListItemText primary={option} />
																	</Box>
																</Box>
															</Grid>
														))}
													</Grid>
												)}

												<Box
													marginTop={2}
													display={'flex'}
													justifyContent={{ xs: 'center', md: 'flex-start' }}>
													<SimButton
														href={'/resources'}
														endIcon={
															<Box
																component={'svg'}
																xmlns='http://www.w3.org/2000/svg'
																fill='none'
																viewBox='0 0 24 24'
																stroke='currentColor'
																width={24}
																height={24}>
																<path
																	strokeLinecap='round'
																	strokeLinejoin='round'
																	strokeWidth={2}
																	d='M17 8l4 4m0 0l-4 4m4-4H3'
																/>
															</Box>
														}>
														Read More
													</SimButton>
												</Box>
											</Box>
										</CardContent>
									</Card>
								</Box>
							))}
						</SimSlider>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export { FSSAutismAssessment };
